if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector("form");
        this.variantIdInput.disabled = false;
        this.form.addEventListener("submit", this.onSubmitHandler.bind(this));
        this.cart =
          document.querySelector("cart-notification") ||
          document.querySelector("cart-drawer");
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector("span");

        if (document.querySelector("cart-drawer"))
          this.submitButton.setAttribute("aria-haspopup", "dialog");

        this.hideErrors = this.dataset.hideErrors === "true";
      }

      get addonSelect() {
        return this.closest("product-info")?.querySelector(
          "[data-addon-select]",
        );
      }

      get addonToggle() {
        return this.closest("product-info")?.querySelector(
          "[data-addon-toggle]",
        );
      }

      getAddonCartItem(formData) {
        const addonVariantId = this.addonSelect?.value;
        const shouldAddAddon =
          this.addonToggle?.checked && !this.addonToggle?.disabled;

        if (!shouldAddAddon || !addonVariantId || this.addonSelect?.disabled) {
          return null;
        }

        return {
          id: Number(addonVariantId),
          quantity: Number(formData.get("quantity") || 1),
        };
      }

      getMainCartItem(formData) {
        const properties = {};

        formData.forEach((value, key) => {
          const propertyMatch = key.match(/^properties\[(.+)\]$/);

          if (propertyMatch) {
            properties[propertyMatch[1]] = value;
          }
        });

        const item = {
          id: Number(formData.get("id")),
          quantity: Number(formData.get("quantity") || 1),
        };

        const sellingPlan = formData.get("selling_plan");
        if (sellingPlan) {
          item.selling_plan = Number(sellingPlan);
        }

        if (Object.keys(properties).length) {
          item.properties = properties;
        }

        return item;
      }

      getCartRequestBody(formData) {
        const addonItem = this.getAddonCartItem(formData);

        if (!addonItem) {
          return formData;
        }

        const body = {
          items: [this.getMainCartItem(formData), addonItem],
        };

        if (this.cart) {
          body.sections = this.cart
            .getSectionsToRender()
            .map((section) => section.id);
          body.sections_url = window.location.pathname;
        }

        return JSON.stringify(body);
      }

      getCartRequestConfig(formData) {
        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";

        const requestBody = this.getCartRequestBody(formData);

        if (requestBody instanceof FormData) {
          delete config.headers["Content-Type"];
          config.body = requestBody;
          return config;
        }

        config.headers["Content-Type"] = "application/json";
        config.body = requestBody;
        return config;
      }

      normalizeCartAddResponse(response, formData) {
        if (response.key || !Array.isArray(response.items)) {
          return response;
        }

        const mainVariantId = Number(formData.get("id"));
        const mainLineItem =
          response.items.find((item) => Number(item.id) === mainVariantId) ||
          response.items[0];

        if (mainLineItem?.key) {
          response.key = mainLineItem.key;
        }

        return response;
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute("aria-disabled") === "true") return;

        this.handleErrorMessage();

        this.submitButton.setAttribute("aria-disabled", true);
        this.submitButton.classList.add("loading");
        this.querySelector(".loading__spinner").classList.remove("hidden");

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            "sections",
            this.cart.getSectionsToRender().map((section) => section.id),
          );
          formData.append("sections_url", window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        const config = this.getCartRequestConfig(formData);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            response = this.normalizeCartAddResponse(response, formData);

            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: "product-form",
                productVariantId: formData.get("id"),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage =
                this.submitButton.querySelector(".sold-out-message");
              if (!soldOutMessage) return;
              this.submitButton.setAttribute("aria-disabled", true);
              this.submitButtonText.classList.add("hidden");
              soldOutMessage.classList.remove("hidden");
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: "product-form",
                productVariantId: formData.get("id"),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest("quick-add-modal");
            if (quickAddModal) {
              document.body.addEventListener(
                "modalClosed",
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true },
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove("loading");
            if (this.cart && this.cart.classList.contains("is-empty"))
              this.cart.classList.remove("is-empty");
            if (!this.error) this.submitButton.removeAttribute("aria-disabled");
            this.querySelector(".loading__spinner").classList.add("hidden");
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(".product-form__error-message-wrapper");
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            ".product-form__error-message",
          );

        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute("disabled", "disabled");
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute("disabled");
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector("[name=id]");
      }
    },
  );
}
